/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import map from "lodash/map";

import { getTheme } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { SearchBox } from "@fluentui/react/lib/SearchBox";
import { Stack, StackItem } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";

import { useAuth } from "Authentication/AuthProvider";
import ListCounts from "Components/Common/ListCounts/ListCounts";
import MostValuableProfessionalList from "Components/MostValuableProfessionalList/MostValuableProfessionalList";
import {
  defaultListingPageSize,
  defaultMvpFilterValues,
  mvpSidebarFilterFields,
} from "Constants/ProfileConstants";
import { BrandLabel } from "Constants/SiteConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
  searchBoxStyles,
  tableStackStyles,
  RDFlexRow,
  RDPaddedFlexRow,
  RDTextStyles,
} from "Containers/Common/CommonContainer.styles";
import SideBarContainer from "Containers/SideBarContainer/SideBarContainer";
import { getClRegions } from "Dataflows/ClRegion/ClRegionAction";
import { loadCommonData } from "Dataflows/CommonData/CommonDataAction";
import { getIndustryFocus } from "Dataflows/IndustryFocus/IndustryFocusAction";
import {
  searchUserProfiles,
  userProfileFilterValueReset,
  userProfileSearchFilterValuesBulkRemove,
  userProfileSearchFilterValueAdd,
  userProfileSearchFilterValueRemove,
  userProfileSearchFilterValueStateProvinceRemove,
  userProfileSearchFilterValueUpdate,
  MVPActionTypes,
} from "Dataflows/MVP/MVPAction";
import {
  getFilteredCount,
  getIsLoadingStatus,
  getPaginationCurrentPage,
  getPaginationPageSize,
  getSearchTextValue,
  getSelectedFilterValues,
  getSortColumn,
  getSortDescending,
  selectTechnologyAreaFilterValuesByAreaGroups,
} from "Dataflows/MVP/Search/MVPUserProfileSearchSelector";
import { getTechnicalExpertise } from "Dataflows/TechnicalExpertise/TechnicalExpertiseAction";
import { getTechnologyAreas } from "Dataflows/TechnologyArea/TechnologyAreaAction";
import { IFilterValue } from "Interfaces/IFilterValue";

/**
 * The Most Valuable Professional Search Container component
 * @method
 * @returns {ReactElement} The Most Valuable Professional Search Container component
 */
const MostValuableProfessionalSearchContainer = (): ReactElement => {
  const { isUserLoggedIn, loading, hasAnyRoles } = useAuth();
  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  const dispatch = useDispatch();
  const theme = getTheme();
  const selectedFilterValues = useSelector(getSelectedFilterValues);
  const pageSize: number = useSelector(getPaginationPageSize);
  const currentPage: number = useSelector(getPaginationCurrentPage);
  const sortColumn = useSelector(getSortColumn);
  const sortDescending = useSelector(getSortDescending);
  const totalCount = useSelector(getFilteredCount);
  const isUserProfilesLoading = useSelector(getIsLoadingStatus);
  const [searchText, setSearchText] = useState(useSelector(getSearchTextValue));

  const handleSearch = (
    searchValue: string,
    selectedUserProfilesFilterValues: IFilterValue[],
    page: number,
    pageSize: number,
    sortColumn?: string,
    sortDescending?: boolean
  ): void => {
    dispatch(
      searchUserProfiles(
        searchValue,
        selectedUserProfilesFilterValues,
        page,
        pageSize,
        sortColumn,
        sortDescending
      )
    );
  };

  /**
   * Call to reset filter values.
   * @method
   */
  const resetFilterValues = (): void => {
    dispatch(userProfileFilterValueReset());

    setSearchText("");
    handleSearch("", defaultMvpFilterValues, 1, defaultListingPageSize);
  };

  useEffect(() => {
    dispatch(getTechnologyAreas(TenantTypesEnum.MVP));
    dispatch(getClRegions());
    dispatch(getIndustryFocus());
    dispatch(getTechnicalExpertise());
    dispatch(loadCommonData());
  }, []);

  useEffect(() => {
    handleSearch(
      searchText,
      selectedFilterValues,
      currentPage,
      pageSize,
      sortColumn,
      sortDescending
    );
  }, [currentPage, pageSize, sortColumn, sortDescending]);

  const [techAreaGroupsRemoved, setTechAreaGroupsRemoved] = useState([""]);

  const addFilterValue = (filter: IFilterValue): MVPActionTypes => {
    setTechAreaGroupsRemoved([""]);
    return dispatch(userProfileSearchFilterValueAdd(filter));
  };

  const removeUserProfilesFilterValue = (filter: IFilterValue): MVPActionTypes =>
    dispatch(userProfileSearchFilterValueRemove(filter));

  const removeMultipleUserProfilesFilterValues = (filters: IFilterValue[]): MVPActionTypes =>
    dispatch(userProfileSearchFilterValuesBulkRemove(filters));

  const updateUserProfilesFilterValue = (filter: IFilterValue): MVPActionTypes =>
    dispatch(userProfileSearchFilterValueUpdate(filter));

  const removeUserProfilesProvinceFilterValue = (): MVPActionTypes =>
    dispatch(userProfileSearchFilterValueStateProvinceRemove());

  const onTechAreaGroupFilterValuesRemovedOrAdded = (filtersNotSelected: IFilterValue[]): void =>
    setTechAreaGroupsRemoved(map(filtersNotSelected, (f) => f.value));

  const techAreasByAreaGroup = useSelector(() =>
    selectTechnologyAreaFilterValuesByAreaGroups(techAreaGroupsRemoved)
  );

  const techAreasToRemove = techAreasByAreaGroup.filter((fl) =>
    selectedFilterValues.some((sfv) => sfv.value === fl.value)
  );

  useEffect(() => {
    if (techAreasToRemove.length > 0) {
      removeMultipleUserProfilesFilterValues(techAreasToRemove);
    }
  }, [techAreasToRemove]);

  return (
    <Stack horizontal styles={containerStackStyles}>
      <StackItem disableShrink>
        <SideBarContainer
          addFilterValue={addFilterValue}
          removeFilterValue={removeUserProfilesFilterValue}
          updateFilterValue={updateUserProfilesFilterValue}
          removeFilterProvinceValue={removeUserProfilesProvinceFilterValue}
          onTechAreaGroupFilterValueChanged={onTechAreaGroupFilterValuesRemovedOrAdded}
          selectedFilterValues={selectedFilterValues}
          filterFields={mvpSidebarFilterFields}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Apply"
            onClick={(): void => handleSearch(searchText, selectedFilterValues, 1, pageSize)}
          />
          <PrimaryButton styles={filterButtonStyles()} text="Reset" onClick={resetFilterValues} />
        </Stack>
      </StackItem>
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <RDPaddedFlexRow>
            <Text styles={RDTextStyles}>{BrandLabel.MVP}</Text>
            <RDFlexRow>
              <ListCounts total={totalCount} loading={isUserProfilesLoading} />
            </RDFlexRow>
          </RDPaddedFlexRow>
          <SearchBox
            placeholder="Search most valuable professionals"
            value={searchText}
            onSearch={(searchValue: string): void =>
              handleSearch(searchValue, selectedFilterValues, 1, pageSize)
            }
            onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void =>
              setSearchText(newValue || "")
            }
            onClear={() => handleSearch("", selectedFilterValues, 1, pageSize)}
            styles={searchBoxStyles}
          />
          <MostValuableProfessionalList />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

export default MostValuableProfessionalSearchContainer;
